import axios, { type AxiosError, type AxiosInstance } from "axios";

export const X_LAST_WRITE_HEADER = "x-last-write";
export const X_LAST_WRITE_STORAGE_KEY = "x-last-write";

export function getApiBasePath(): string {
  const base = import.meta.env.VITE_API_BASE_PATH ?? "";
  return base.replace(/\/+$/, "");
}

export function isKnmiApiUrl(url: string | URL): boolean {
  const base = getApiBasePath();
  if (!base) return false;

  const resolved = typeof url === "string" ? url : url.href;
  return resolved.startsWith(base);
}

export function getXLastWrite(): string | undefined {
  if (typeof sessionStorage === "undefined") return undefined;

  const raw = sessionStorage.getItem(X_LAST_WRITE_STORAGE_KEY);
  if (!raw) return undefined;

  const trimmed = raw.trim();
  return trimmed || undefined;
}

export function setXLastWrite(value: string): void {
  if (typeof sessionStorage === "undefined") return;

  const trimmed = value.trim();
  if (!trimmed) return;

  sessionStorage.setItem(X_LAST_WRITE_STORAGE_KEY, trimmed);
}

export function applyXLastWriteToHeaders(headers: Headers): void {
  const stored = getXLastWrite();
  if (stored) {
    headers.set(X_LAST_WRITE_HEADER, stored);
  }
}

function readHeaderValue(headers: unknown, name: string): string | undefined {
  if (!headers || typeof headers !== "object") return undefined;

  if ("get" in headers && typeof headers.get === "function") {
    const value = (headers as { get: (header: string) => string | null }).get(name);
    return value ?? undefined;
  }

  const record = headers as Record<string, unknown>;
  const direct = record[name] ?? record[name.toLowerCase()];
  return typeof direct === "string" ? direct : undefined;
}

export function captureXLastWriteFromHeaders(headers: unknown): void {
  const raw = readHeaderValue(headers, X_LAST_WRITE_HEADER);
  if (!raw) return;

  const trimmed = raw.trim();
  if (trimmed) {
    setXLastWrite(trimmed);
  }
}

function isKnmiApiRequest(config: {
  baseURL?: string;
  url?: string;
  params?: unknown;
}): boolean {
  try {
    return isKnmiApiUrl(axios.getUri(config));
  } catch {
    return false;
  }
}

export function installAxiosXLastWriteMiddleware(instance: AxiosInstance): void {
  instance.interceptors.request.use((config) => {
    if (!isKnmiApiRequest(config)) return config;

    const stored = getXLastWrite();
    if (stored) {
      config.headers.set(X_LAST_WRITE_HEADER, stored);
    }

    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      if (isKnmiApiRequest(response.config)) {
        captureXLastWriteFromHeaders(response.headers);
      }
      return response;
    },
    (error: AxiosError) => {
      if (error.config && isKnmiApiRequest(error.config) && error.response) {
        captureXLastWriteFromHeaders(error.response.headers);
      }
      return Promise.reject(error);
    },
  );
}

function resolveFetchUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") {
    return resolveUrlString(input);
  }

  if (input instanceof URL) {
    return input.href;
  }

  return resolveUrlString(input.url);
}

function resolveUrlString(url: string): string {
  try {
    return new URL(url).href;
  } catch {
    if (typeof window !== "undefined") {
      return new URL(url, window.location.origin).href;
    }
    return url;
  }
}

function fetchWithXLastWrite(
  originalFetch: typeof fetch,
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const url = resolveFetchUrl(input);
  if (!isKnmiApiUrl(url)) {
    return originalFetch(input, init);
  }

  const stored = getXLastWrite();

  if (input instanceof Request) {
    const headers = new Headers(input.headers);
    if (init?.headers) {
      new Headers(init.headers).forEach((value, key) => headers.set(key, value));
    }
    if (stored) {
      applyXLastWriteToHeaders(headers);
    }

    const request = new Request(input, { ...init, headers });
    return originalFetch(request).then((response) => {
      captureXLastWriteFromHeaders(response.headers);
      return response;
    });
  }

  const headers = new Headers(init?.headers);
  if (stored) {
    applyXLastWriteToHeaders(headers);
  }

  return originalFetch(input, { ...init, headers }).then((response) => {
    captureXLastWriteFromHeaders(response.headers);
    return response;
  });
}

let fetchMiddlewareInstalled = false;

export function installFetchXLastWriteMiddleware(): void {
  if (fetchMiddlewareInstalled || typeof globalThis.fetch !== "function") return;

  fetchMiddlewareInstalled = true;
  const originalFetch = globalThis.fetch.bind(globalThis);

  globalThis.fetch = (input, init) => fetchWithXLastWrite(originalFetch, input, init);
}
